import type { CSSProperties, ReactNode } from 'react';

/**
 * Checkbox as the prototype draws it: 1.5px border, radius 4 (5 on the
 * 20px thumb version), ink fill when on, white check (stroke 3.5), or a
 * white 8×2 bar for the indeterminate state.
 */
export function Checkbox({
  checked,
  indeterminate,
  onToggle,
  size = 15,
  title,
  label,
  raised,
  style,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onToggle: (e: React.MouseEvent | React.KeyboardEvent) => void;
  size?: 15 | 16 | 20;
  title?: string;
  /** What ticking it selects, for a screen reader: "Select Breast IHC panel".
   *  Falls back to `title`. Without either a focusable checkbox is announced
   *  as just "checkbox", with nothing saying which row a bulk Delete takes. */
  label?: string;
  /** the Stack Thumb version: radius 5, soft shadow, translucent white when off */
  raised?: boolean;
  style?: CSSProperties;
}) {
  const on = checked || !!indeterminate;
  return (
    <div
      // A real checkbox, not a styled div that happens to take clicks. It was
      // the second: no role, no state, no way to reach it without a mouse —
      // which matters more now that ticking one is the way to the toolbar that
      // DELETES collections and the studies inside them. Space and Enter are
      // what a checkbox answers to, and both stop at this element so the row
      // underneath does not also open.
      role="checkbox"
      aria-checked={indeterminate && !checked ? 'mixed' : checked}
      aria-label={label ?? title}
      tabIndex={0}
      title={title}
      onKeyDown={(e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        // Once per press. A held key repeats keydown, and toggling on each
        // one flipped the box on and off at the repeat rate.
        if (!e.repeat) onToggle(e);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(e);
      }}
      style={{
        width: size,
        height: size,
        border: `1.5px solid ${on ? '#0f0f10' : '#d4d4d2'}`,
        borderRadius: raised ? 5 : 4,
        background: on ? '#0f0f10' : raised ? 'rgba(255,255,255,.9)' : '#fff',
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
        flex: 'none',
        boxShadow: raised ? '0 1px 2px rgba(0,0,0,.08)' : undefined,
        ...style,
      }}
    >
      {indeterminate && !checked ? (
        <div style={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />
      ) : checked ? (
        <svg width={size === 20 ? 11 : 10} height={size === 20 ? 11 : 10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" aria-hidden>
          <path d="m5 12 5 5L20 7" />
        </svg>
      ) : null}
    </div>
  );
}

/** 36×20 switch, knob 16, ink when on, #d4d4d2 when off. */
export function Switch({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      style={{ width: 36, height: 20, borderRadius: 10, background: on ? '#0f0f10' : '#d4d4d2', position: 'relative', cursor: 'pointer', flex: 'none', transition: 'background .15s' }}
    >
      <div style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.2)', transition: 'left .15s' }} />
    </div>
  );
}

/**
 * Segmented control: track #f2f2f0 radius 8 padding 3, active segment white
 * with a soft shadow (Explore sort, Usage sort). Segment height 28 (Explore)
 * or 24 (Usage, with a bordered #fafaf9 track).
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  segH = 28,
  bordered,
}: {
  value: T;
  options: ReadonlyArray<{ id: T; label: string }>;
  onChange: (v: T) => void;
  segH?: 24 | 28;
  bordered?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: bordered ? 4 : 2,
        padding: 3,
        borderRadius: 8,
        background: bordered ? '#fafaf9' : '#f2f2f0',
        border: bordered ? '1px solid #ebebea' : undefined,
      }}
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(o.id);
            }}
            style={{
              height: segH,
              padding: segH === 24 ? '0 9px' : '0 10px',
              border: 0,
              borderRadius: segH === 24 ? 5 : 6,
              background: on ? '#fff' : 'transparent',
              color: on ? '#0f0f10' : '#6b6b70',
              boxShadow: on ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
              fontSize: segH === 24 ? 12 : 12.5,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Pill chip 30px (Explore organs): active filled ink. */
export function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        height: 30,
        padding: '0 11px',
        border: `1px solid ${on ? '#0f0f10' : '#e3e3e1'}`,
        borderRadius: 999,
        background: on ? '#0f0f10' : '#fff',
        color: on ? '#fff' : '#3f3f44',
        fontSize: 12.5,
        fontWeight: 500,
        cursor: 'pointer',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        boxShadow: on ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
      }}
    >
      {label}
    </button>
  );
}

/** Small uppercase badge (DEFAULT, INVITED, CURRENT, FEATURED). */
export function Badge({ children, tone = 'outline', style }: { children: ReactNode; tone?: 'outline' | 'amber' | 'ink' | 'white'; style?: CSSProperties }) {
  const t: CSSProperties =
    tone === 'amber'
      ? { color: '#8a6d1f', background: '#fbf3d9', padding: '1px 6px', borderRadius: 4 }
      : tone === 'ink'
        ? { color: '#fff', background: '#0f0f10', padding: '2px 7px', borderRadius: 4, letterSpacing: '.05em' }
        : tone === 'white'
          ? { background: 'rgba(255,255,255,.92)', height: 22, padding: '0 8px', borderRadius: 5, lineHeight: '22px', letterSpacing: '.05em', fontSize: 11 }
          : { color: '#6b6b70', padding: '1px 5px', border: '1px solid #e3e3e1', borderRadius: 4 };
  return <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', whiteSpace: 'nowrap', ...t, ...style }}>{children}</span>;
}

/** The amber notice (PHI warning): 12.5px, triangle icon #9a6400. */
export function Notice({ children, tone = 'warn', style }: { children: ReactNode; tone?: 'warn' | 'danger'; style?: CSSProperties }) {
  const warn = tone === 'warn';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: warn ? 8 : 12,
        padding: warn ? '9px 12px' : '12px 14px',
        borderRadius: warn ? 8 : 9,
        background: warn ? '#fff8e6' : '#fdf1f0',
        border: `1px solid ${warn ? '#f3dea3' : '#f3c9c5'}`,
        color: warn ? '#5c4400' : '#8f1d16',
        fontSize: 12.5,
        lineHeight: warn ? undefined : 1.45,
        ...style,
      }}
    >
      {warn ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9a6400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }} aria-hidden>
          <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d9342b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }} aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      )}
      {children}
    </div>
  );
}

/**
 * Pager.dc.html. Shown only when total > pageSize. Range label 12.5px
 * #8a8a8f; buttons 28px, current filled ink, gaps as "…", mono numerals.
 */
export function Pager({ total, pageSize = 12, page, noun = 'items', onChange }: { total: number; pageSize?: number; page: number; noun?: string; onChange: (n: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(Math.max(1, page), pages);
  if (total <= pageSize) return null;
  const nums: number[] = [];
  for (let i = 1; i <= pages; i++) if (i === 1 || i === pages || Math.abs(i - cur) <= 1) nums.push(i);
  const from = total ? (cur - 1) * pageSize + 1 : 0;
  const to = Math.min(total, cur * pageSize);
  const go = (n: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (n < 1 || n > pages || n === cur) return;
    onChange(n);
  };
  const nav = (dir: 'prev' | 'next') => {
    const ok = dir === 'prev' ? cur > 1 : cur < pages;
    return (
      <button
        type="button"
        className="tr-hover-soft"
        onClick={go(dir === 'prev' ? cur - 1 : cur + 1)}
        style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', border: '1px solid #e3e3e1', borderRadius: 6, background: '#fff', color: ok ? '#0f0f10' : '#c4c4c2', cursor: ok ? 'pointer' : 'default' }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
          <path d={dir === 'prev' ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6'} />
        </svg>
      </button>
    );
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, paddingTop: 6 }}>
      {/* "1 to 12 of 57": the bundle's own en dash is forbidden by its copy rule */}
      <span style={{ fontSize: 12.5, color: '#8a8a8f' }}>{from} to {to} of {total} {noun}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {nav('prev')}
        {nums.map((n, i) => (
          <span key={n} style={{ display: 'contents' }}>
            {i > 0 && n - nums[i - 1] > 1 ? <span style={{ width: 20, textAlign: 'center', fontSize: 12.5, color: '#8a8a8f' }}>…</span> : null}
            <button
              type="button"
              className="tr-hover-soft"
              onClick={go(n)}
              style={{
                minWidth: 28,
                height: 28,
                padding: '0 6px',
                border: `1px solid ${n === cur ? '#0f0f10' : '#e3e3e1'}`,
                borderRadius: 6,
                background: n === cur ? '#0f0f10' : '#fff',
                color: n === cur ? '#fff' : '#0f0f10',
                fontSize: 12.5,
                fontWeight: 500,
                fontFamily: "'JetBrains Mono', monospace",
                cursor: 'pointer',
              }}
            >
              {n}
            </button>
          </span>
        ))}
        {nav('next')}
      </div>
    </div>
  );
}
